/**
 * Item type definitions for template items
 * Enhanced to support the new template-repetition architecture
 */

import ItemRenderer from "../components/items/ItemRenderer";

// Core item types
export const ITEM_TYPES = {
  CHECKBOX: "checkbox",
  DATE_TEXT: "date-text",
  TEXT_MULTI: "text-multi",
  NUMBER: "number",
  SELECT: "select",
  DATE: "date",
  // FILE_UPLOAD: "file-upload",
  // SIGNATURE: "signature",
  // PHOTO: "photo",
  // RATING: "rating",
};

// Item status constants for tracking completion
export const ITEM_STATUS = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  SKIPPED: "skipped",
  FAILED: "failed",
};

// Page status constants
export const PAGE_STATUS = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
};

// Checklist instance status constants
export const CHECKLIST_STATUS = {
  DRAFT: "draft",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  // SUBMITTED: "submitted",
};

// User roles
export const USER_ROLES = {
  MANAGER: "MANAGER",
  TECHNICIAN: "TECHNICIAN",
};

// Enhanced item type configurations
export const ITEM_TYPE_CONFIGS = {
  [ITEM_TYPES.CHECKBOX]: {
    id: ITEM_TYPES.CHECKBOX,
    name: "Header Item",
    description: "Simple Header",
    icon: "Header",
    category: "basic",
    defaultValue: {
      completed: false,
    },
    defaultData: {
      text: "",
      completed: false,
    },
    validation: {
      required: false,
    },
    metadata: {
      supportsBulkEdit: true,
      supportsConditionalLogic: true,
      dataType: "boolean",
    },
  },
  [ITEM_TYPES.DATE_TEXT]: {
    id: ITEM_TYPES.DATE_TEXT,
    name: "Text Item",
    description: "Item with text input",
    icon: "TextFields",
    category: "basic",
    defaultValue: {
      date: null,
      text: "",
    },
    defaultData: {
      description: "",
      date: null,
      textValue: "",
      completed: false,
    },
    validation: {
      required: false,
      dateRequired: false,
      textRequired: false,
      textMaxLength: 500,
    },
    metadata: {
      supportsBulkEdit: false,
      supportsConditionalLogic: true,
      dataType: "object",
    },
  },
  [ITEM_TYPES.SELECT]: {
    id: ITEM_TYPES.SELECT,
    name: "Dropdown Select",
    description: "Single selection from options",
    icon: "ArrowDropDown",
    category: "basic",
    defaultValue: {
      date: null,
      text: "",
    },
    defaultData: {
      description: "",
      date: null,
      textValue: "",
      completed: false,
    },
    validation: {
      required: false,
      options: [],
    },
    metadata: {
      supportsBulkEdit: true,
      supportsConditionalLogic: true,
      dataType: "object",
    },
  },
  [ITEM_TYPES.NUMBER]: {
    id: ITEM_TYPES.NUMBER,
    name: "Number Input",
    description: "Numeric input with validation",
    icon: "Numbers",
    category: "basic",
    defaultValue: null,
    defaultData: {
      value: null,
      completed: false,
    },
    validation: {
      required: false,
      min: null,
      max: null,
      step: 1,
      decimals: 0,
    },
    metadata: {
      supportsBulkEdit: true,
      supportsConditionalLogic: true,
      dataType: "number",
    },
  },
  [ITEM_TYPES.TEXT_MULTI]: {
    id: ITEM_TYPES.TEXT_MULTI,
    name: "Text Area",
    description: "Multi-line text input",
    icon: "Notes",
    category: "basic",
    defaultValue: "",
    defaultData: {
      text: "",
      completed: false,
    },
    validation: {
      required: false,
      minLength: 0,
      maxLength: 1000,
      pattern: null,
    },
    metadata: {
      supportsBulkEdit: true,
      supportsConditionalLogic: true,
      dataType: "string",
    },
  },
  [ITEM_TYPES.DATE]: {
    id: ITEM_TYPES.DATE,
    name: "Date Item",
    description: "Item with description and date selector",
    icon: "Event",
    category: "basic",
    defaultValue: {
      date: null,
      text: "",
    },
    defaultData: {
      description: "",
      date: null,
      textValue: "",
      completed: false,
    },
    validation: {
      required: false,
      dateRequired: false,
      textRequired: false,
      textMaxLength: 500,
    },
    metadata: {
      supportsBulkEdit: false,
      supportsConditionalLogic: true,
      dataType: "object",
    },
  },
};

// Helper functions
export const getItemTypeConfig = (type) => {
  return ITEM_TYPE_CONFIGS[type] || ITEM_TYPE_CONFIGS[ITEM_TYPES.CHECKBOX];
};

export const getAvailableItemTypes = () => {
  return Object.keys(ITEM_TYPE_CONFIGS);
};

export const getItemTypesByCategory = () => {
  const categories = {};
  Object.values(ITEM_TYPE_CONFIGS).forEach((config) => {
    if (!categories[config.category]) {
      categories[config.category] = [];
    }
    categories[config.category].push(config);
  });
  return categories;
};

export const createNewItem = (type, overrides = {}) => {
  const config = getItemTypeConfig(type);
  return {
    id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
    label: "",
    required: false,
    order: 0,
    config: {},
    ...config.defaultData,
    ...overrides,
  };
};

/**
 * Creates a new template item definition
 * @param {string} type - Item type from ITEM_TYPES
 * @param {Object} options - Item configuration options
 * @returns {Object} Template item definition
 */
export const createTemplateItem = (type, options = {}) => {
  const config = getItemTypeConfig(type);
  const templateItem = {
    id:
      options.id ||
      `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type,
    defaultValue: options.defaultValue || {},
    defaultData: options.defaultData || {},
    label: options.label || "",
    required: options.required || false,
    order: options.order || 0,
    config: {
      ...config.validation,
      ...options.config,
    },
  };

  // For SELECT items, ensure optionList is available at the top level for easy access
  if (type === ITEM_TYPES.SELECT && options.config?.optionList) {
    templateItem.config = {
      ...templateItem.config,
      ...options.config.optionList,
    };
  }
  if (type === ITEM_TYPES.NUMBER && options.config?.options) {
    templateItem.config = {
      ...templateItem.config,
      ...options.config.options,
    };
  }
  return templateItem;
};

/**
 * Creates a new item instance for checklist pages
 * @param {Object} templateItem - Template item definition
 * @param {*} value - Initial value for the item
 * @returns {Object} Item instance
 */
export const createItemInstance = (templateItem, value = null) => {
  const config = getItemTypeConfig(templateItem.type);
  return {
    id: `item_instance_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`,
    templateItemId: templateItem.id,
    description: templateItem.description,
    type: templateItem.type,
    defaultValue: templateItem.defaultValue || {},
    defaultData: templateItem.defaultData || {},
    value: value !== null ? value : config.defaultValue,
    status: ITEM_STATUS.NOT_STARTED,
    completedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

/**
 * Validates an item value against its configuration
 * @param {Object} templateItem - Template item definition
 * @param {*} value - Value to validate
 * @returns {Object} Validation result { isValid: boolean, errors: string[] }
 */
export const validateItemValue = (templateItem, value) => {
  const config = getItemTypeConfig(templateItem.type);
  const validation = { ...config.validation, ...templateItem.config };
  const errors = [];

  // Check required
  if (
    validation.required &&
    (value === null || value === undefined || value === "")
  ) {
    errors.push("This field is required");
  }

  // Type-specific validation
  switch (templateItem.type) {
    case ITEM_TYPES.TEXT:
      if (value && typeof value === "string") {
        if (validation.minLength && value.length < validation.minLength) {
          errors.push(`Minimum length is ${validation.minLength} characters`);
        }
        if (validation.maxLength && value.length > validation.maxLength) {
          errors.push(`Maximum length is ${validation.maxLength} characters`);
        }
        if (validation.pattern && !new RegExp(validation.pattern).test(value)) {
          errors.push("Value does not match required pattern");
        }
      }
      break;

    case ITEM_TYPES.NUMBER:
      if (value !== null && value !== undefined) {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          errors.push("Must be a valid number");
        } else {
          if (validation.min !== null && numValue < validation.min) {
            errors.push(`Minimum value is ${validation.min}`);
          }
          if (validation.max !== null && numValue > validation.max) {
            errors.push(`Maximum value is ${validation.max}`);
          }
        }
      }
      break;

    case ITEM_TYPES.MULTI_SELECT:
      if (Array.isArray(value)) {
        if (
          validation.minSelections &&
          value.length < validation.minSelections
        ) {
          errors.push(
            `Minimum ${validation.minSelections} selections required`
          );
        }
        if (
          validation.maxSelections &&
          value.length > validation.maxSelections
        ) {
          errors.push(`Maximum ${validation.maxSelections} selections allowed`);
        }
      }
      break;

    case ITEM_TYPES.DATE_TEXT:
      if (value && typeof value === "object") {
        if (validation.dateRequired && !value.date) {
          errors.push("Date is required");
        }
        if (validation.textRequired && !value.text) {
          errors.push("Text is required");
        }
        if (
          value.text &&
          validation.textMaxLength &&
          value.text.length > validation.textMaxLength
        ) {
          errors.push(
            `Text maximum length is ${validation.textMaxLength} characters`
          );
        }
      }
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
